import {
  Button,
  ButtonGroup,
  Card,
  Flexbox,
  Heading,
  Positions,
  Sizes,
  Text,
  TextElementTypes,
} from '@sede-enterprise/shell-ds-react-framework';
import {
  Download,
  FullScreen,
  FullScreenExit,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { getSourceKey } from '@see-power-pft-common-js/utils/sourceKey';
import { FullScreenModal } from 'components/FullScreenModal/FullScreenModal';
import { FullScreenModalChartWrapper } from 'components/FullScreenModal/FullScreenModal.styled';
import { InfoCircleTooltip } from 'components/Tooltip/InfoCircleTooltip';
import { AvailabilityChart } from 'pages/Availability/common/AvailabilityChart/AvailabilityChart';
import { AvailabilityChartType } from 'pages/Availability/common/AvailabilityChart/types';
import { AvailabilityDownloadModal } from 'pages/Availability/common/Modals/AvailabilityDownloadModal';
import { AvailabilityChartData } from 'pages/Availability/types';
import { useEffect, useState } from 'react';
import { useStore } from 'store';
import { Timezone } from 'types/timezone';
import { checkAvailabilityDownloadEnabled } from 'utils/features';

interface AvailabilityChartCardProps {
  style?: React.CSSProperties;
  data: AvailabilityChartData | null;
  overlayMessage?: string | null;
  timezone: Timezone;
}

export const AvailabilityChartCard = (props: AvailabilityChartCardProps) => {
  const [appTheme] = useStore((state) => [state.appTheme]);
  const selectedSiteData = useStore((state) => state.selectedSiteData);

  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [chartType, setChartType] = useState<AvailabilityChartType>('power');
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const handleSetChartType = (value: string) => {
    setChartType(value as AvailabilityChartType);
  };

  const toggleFullscreen = () => setIsFullScreenOpen((current) => !current);

  const isAvailabilityDownloadEnabled = checkAvailabilityDownloadEnabled();

  const downloadButton =
    isAvailabilityDownloadEnabled && selectedSiteData ? (
      <Button
        variant="outlined"
        data-testid="availability-chart-download-button"
        icon={<Download />}
        iconPosition={Positions.Right}
        onClick={() => setIsDownloadModalOpen(true)}
        style={{ flexShrink: 0 }}
      >
        Download
      </Button>
    ) : null;

  const fullScreenButton = (
    <Button
      variant="outlined"
      data-testid="availability-chart-fullscreen-button"
      icon={isFullScreenOpen ? <FullScreenExit /> : <FullScreen />}
      onClick={toggleFullscreen}
      iconOnly
      style={{ flexShrink: 0 }}
    />
  );

  const chartData = props.data ? props.data[chartType] : null;
  const energyPowerButton = (
    <ButtonGroup
      type="radio"
      size={Sizes.Medium}
      selectedKey={chartType}
      onChange={handleSetChartType}
      items={[
        {
          label: 'Power (MW)',
          value: 'power',
        },
        {
          label: 'Energy (MWh)',
          value: 'energy',
        },
      ]}
    />
  );

  const headerContent = (
    <Flexbox
      style={{ padding: 24, width: '100%' }}
      justifyContent="space-between"
    >
      <Flexbox
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        style={{ width: '100%' }}
        gap="16px"
      >
        <Flexbox alignItems="center" style={{ flexShrink: 0 }}>
          <Heading
            type={TextElementTypes.Span}
            bold
            level={4}
            prominence={'strong'}
            style={{ paddingRight: '5px' }}
          >
            Overview
          </Heading>
          <InfoCircleTooltip
            tooltip={
              <Text type="span" size={Sizes.Small}>
                This graph shows what is available at the site over time in
                either power (MW) or energy (MWh) and updates as new
                availability reductions are made.
              </Text>
            }
            infoCircleStyle={{ marginTop: '2px' }}
          />
        </Flexbox>
        <Flexbox alignItems={'center'} gap={'16px'}>
          {energyPowerButton}
          {fullScreenButton}
          {downloadButton}
        </Flexbox>
      </Flexbox>
    </Flexbox>
  );

  // prevent background scrolling when modal open
  useEffect(() => {
    if (isFullScreenOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isFullScreenOpen]);

  const fullScreenModal = (
    <FullScreenModal
      open={isFullScreenOpen}
      $narrowMobile={false}
      $mobileLandscape={false}
      onClose={toggleFullscreen}
      header={{
        content: (
          <Flexbox
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            style={{
              width: '100%',
            }}
            gap="16px"
          >
            {headerContent}
          </Flexbox>
        ),
      }}
      closeButton={false}
      contentScrollable={false}
      padding={false}
      centered
      size={Sizes.Large}
      $showMask={false}
      $appTheme={appTheme}
      focusTriggerAfterClose
    >
      <FullScreenModalChartWrapper $narrowMobile={false}>
        <AvailabilityChart
          type={chartType}
          data={chartData}
          timezone={props.timezone}
        />
      </FullScreenModalChartWrapper>
    </FullScreenModal>
  );

  return (
    <Card
      header={{
        content: headerContent,
      }}
      style={props.style ?? {}}
    >
      {props.overlayMessage !== null ? (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            height: '200px',
            fontSize: '14px',
          }}
        >
          <p>{props.overlayMessage}</p>
        </div>
      ) : (
        <>
          <AvailabilityChart
            type={chartType}
            data={chartData}
            timezone={props.timezone}
          />
          {fullScreenModal}
        </>
      )}
      {isAvailabilityDownloadEnabled &&
        selectedSiteData &&
        isDownloadModalOpen && (
          <AvailabilityDownloadModal
            showModal
            onClose={() => setIsDownloadModalOpen(false)}
            siteSourceKey={getSourceKey(selectedSiteData)}
          />
        )}
    </Card>
  );
};
